import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Lock, Phone, MapPin, Eye, EyeOff, ArrowLeft, Gift } from 'lucide-react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import OtpVerification from '../components/OtpVerification';
import BrandLogo from '../components/BrandLogo';

export default function Register() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirmPw: '',
    street: '', city: '', state: '', pincode: '',
    referralCode: searchParams.get('ref') || ''
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [otpEmail, setOtpEmail] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPw) return setError('Passwords do not match');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true);
    try {
      const res = await API.post('/auth/register/customer', {
        name: form.name, email: form.email, phone: form.phone, password: form.password,
        address: { street: form.street, city: form.city, state: form.state, pincode: form.pincode }
        , referralCode: form.referralCode
      });
      setOtpEmail(res.data.email || form.email);
      toast.success('Account created. OTP sent to your email.');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page auth-register-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1A1A1A 0%,#2D2D2D 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>

      {/* Back to Home */}
      <div className="auth-back" style={{ width: '100%', maxWidth: 520, marginBottom: 12 }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '7px 14px', color: '#9CA3AF', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          <ArrowLeft size={15} /> Back to Home
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} className="fade-in auth-card auth-register-card">
        {otpEmail ? (
          <OtpVerification
            email={otpEmail}
            title="Verify Your Account"
            subtitle="We sent a 6 digit OTP to your email. Verify to start ordering materials."
            onVerified={() => navigate('/shops')}
            onBack={() => setOtpEmail('')}
          />
        ) : (
        <>
        <div className="auth-header" style={{ textAlign: 'center', marginBottom: 24 }}>
          <BrandLogo size={56} radius={16} />
          <h1 style={{ fontSize: 21, fontWeight: 700, marginBottom: 4 }}>Create Account</h1>
          <p style={{ color: 'var(--gray)', fontSize: 14 }}>Join Material Stock and order materials easily</p>
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name & Phone */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Full Name</label>
              <div style={{ position: 'relative' }}>
                <User size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38 }} placeholder="Your full name" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Phone</label>
              <div style={{ position: 'relative' }}>
                <Phone size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38 }} placeholder="10-digit number" value={form.phone} onChange={e => set('phone', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input className="form-input" style={{ paddingLeft: 38 }} type="email" placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} required />
            </div>
          </div>

          {/* Passwords */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38, paddingRight: 38 }} type={showPw ? 'text' : 'password'}
                  placeholder="Min 6 chars" value={form.password} onChange={e => set('password', e.target.value)} required />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', display: 'flex' }}>
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={14} color="var(--gray)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 38 }} type="password"
                  placeholder="Repeat password" value={form.confirmPw} onChange={e => set('confirmPw', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* Address */}
          <div style={{ background: 'var(--gray-light)', borderRadius: 10, padding: '14px 14px 6px', marginBottom: 14 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
              <MapPin size={13} color="var(--orange)" /> Delivery Address
            </label>
            <div className="form-group">
              <input className="form-input" placeholder="Street / Area" value={form.street} onChange={e => set('street', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input className="form-input" placeholder="City *" value={form.city} onChange={e => set('city', e.target.value)} required style={{ flex: '1 1 110px', marginBottom: 10 }} />
              <input className="form-input" placeholder="State *" value={form.state} onChange={e => set('state', e.target.value)} required style={{ flex: '1 1 90px', marginBottom: 10 }} />
              <input className="form-input" placeholder="Pincode *" value={form.pincode} onChange={e => set('pincode', e.target.value)} required style={{ flex: '0 0 90px', marginBottom: 10 }} />
            </div>
          </div>

          {/* Referral */}
          <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Gift size={13} color="var(--green)" /> Referral Code
            </label>
            <input className="form-input" placeholder="Optional referral code" value={form.referralCode} onChange={e => set('referralCode', e.target.value.toUpperCase())} />
            <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 6 }}>
              Enter a friend's code so their referral reward is credited.
            </div>
          </div>

          <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '13px', fontSize: 15 }} disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--gray)', marginTop: 16 }}>
          Already have an account? <Link to="/login" style={{ color: 'var(--orange)', fontWeight: 600 }}>Sign in</Link>
        </p>
        <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--gray)', marginTop: 6 }}>
          Are you a supplier? <Link to="/register/owner" style={{ color: 'var(--orange)', fontWeight: 600 }}>Register shop</Link>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
